/**
 * MyDataHub.jsx — iter657
 * Top-of-BHI quick-access card showing what data the patient has supplied and
 * where to add what's missing. Surfaces all input pages in one place.
 *
 * Status indicators are read directly from the BHI snapshot returned by
 * /api/breast-intelligence/snapshot — no extra calls.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Database, ChevronRight, ScanLine, FlaskConical, ClipboardList, FileText,
  CheckCircle2, Circle, AlertCircle, Dna,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import UnifiedDocumentUpload from "@/components/breast_intel/UnifiedDocumentUpload";

const TX = {
  bg: {
    title: "📥 Моите данни",
    subtitle:
      "Колкото повече данни добавите — толкова по-точно е представянето. Всичко е под Ваш контрол и можете да изтриете по всяко време.",
    expand: "Покажи опции",
    collapse: "Скрий",
    mammogram: {
      label: "Плътност на гърдата (BI-RADS)",
      desc: "Въведете ръчно BI-RADS A/B/C/D категорията от Вашия мамографски доклад. Качване на самата снимка вече не се изисква.",
      cta: "Въведи BI-RADS",
      route: "#manual-birads-card",
    },
    labs: {
      label: "Лабораторни изследвания и хормони",
      desc: "Качете PDF/снимка на изследвания (hsCRP, HOMA-IR, vit D, TSH, естрадиол, прогестерон, FSH, LH, пролактин, SHBG, DHEA-S, AMH, кортизол…)",
      cta: "Качи лаборатории",
      route: "/dashboard/analyses",
    },
    profile: {
      label: "Заболявания, lifestyle и анамнеза",
      desc: "Фиброаденом, кисти, фиброкистична, mastalgia, atypical hyperplasia, фамилна анамнеза, тегло, сън, активност",
      cta: "Редактирай профил",
      route: "/dashboard/profile",
    },
    epicrisis: {
      label: "Епикризи и доклади",
      desc: "Качете епикризи от лекар (PDF, снимка). Ще се прикачат към профила Ви за бъдещи консултации",
      route: "/dashboard/analyses?tab=lab",
      cta: "Качи епикриза",
    },
    statusComplete:  "Добавени",
    statusPartial:   "Частично",
    statusMissing:   "Липсват",
  },
  en: {
    title: "📥 My Data",
    subtitle:
      "The more data you add the more precise the picture. Everything is yours — delete any time.",
    expand: "Show options",
    collapse: "Hide",
    mammogram: {
      label: "Breast density (BI-RADS)",
      desc: "Enter your BI-RADS A/B/C/D category manually from your mammography report. Image upload is no longer required.",
      cta: "Enter BI-RADS",
      route: "#manual-birads-card",
    },
    labs: {
      label: "Lab tests & serum hormones",
      desc: "Upload lab PDF/photos (hsCRP, HOMA-IR, vit D, TSH, estradiol, progesterone, FSH, LH, prolactin, SHBG, DHEA-S, AMH, cortisol, …)",
      cta: "Upload labs",
      route: "/dashboard/analyses",
    },
    profile: {
      label: "Conditions, lifestyle & history",
      desc: "Fibroadenoma, cysts, fibrocystic disease, mastalgia, atypical hyperplasia, family history, weight, sleep, activity",
      cta: "Edit profile",
      route: "/dashboard/profile",
    },
    epicrisis: {
      label: "Epicrises & physician reports",
      desc: "Upload doctor's epicrises (PDF, photo). Attached to your profile for future consultations",
      cta: "Upload epicrisis",
      route: "/dashboard/analyses?tab=lab",
    },
    statusComplete: "Added",
    statusPartial:  "Partial",
    statusMissing:  "Missing",
  },
  es: {
    title: "📥 Mis datos",
    subtitle:
      "Cuantos más datos agregue más precisa será la imagen. Todo es suyo — puede borrarlo cuando quiera.",
    expand: "Mostrar opciones",
    collapse: "Ocultar",
    mammogram: {
      label: "Densidad mamaria (BI-RADS)",
      desc: "Ingrese manualmente la categoría BI-RADS A/B/C/D de su informe mamográfico. Ya no se requiere subir la imagen.",
      cta: "Introducir BI-RADS",
      route: "#manual-birads-card",
    },
    labs: {
      label: "Análisis de laboratorio",
      desc: "Suba PDF/fotos (hsCRP, HOMA-IR, vit D, omega-3, estradiol, …)",
      cta: "Subir análisis",
      route: "/dashboard/analyses",
    },
    profile: {
      label: "Condiciones, estilo de vida e historia",
      desc: "Fibroadenoma, quistes, enfermedad fibroquística, mastalgia, hiperplasia atípica, antecedentes familiares, peso, sueño, actividad",
      cta: "Editar perfil",
      route: "/dashboard/profile",
    },
    epicrisis: {
      label: "Epicrisis e informes",
      desc: "Suba epicrisis del médico (PDF, foto). Se adjuntan a su perfil para futuras consultas",
      cta: "Subir epicrisis",
      route: "/dashboard/analyses?tab=lab",
    },
    statusComplete: "Añadidos",
    statusPartial:  "Parcial",
    statusMissing:  "Faltan",
  },
};

function StatusPill({ status, t, isDark }) {
  const cfg = {
    complete: { Icon: CheckCircle2, cls: "text-emerald-500", label: t.statusComplete },
    partial:  { Icon: AlertCircle,  cls: "text-amber-500",   label: t.statusPartial  },
    missing:  { Icon: Circle,       cls: "text-slate-400",   label: t.statusMissing  },
  }[status] || { Icon: Circle, cls: "text-slate-400", label: t.statusMissing };
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

export default function MyDataHub({ snapshot, lang = "bg", onSpeclabUploaded }) {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadHint, setUploadHint] = useState("");
  // iter707b — optimistic local override flipped by a window event so the
  // status pill turns green INSTANTLY when the user saves a BI-RADS down the
  // page, without waiting for the parent snapshot to refetch.
  const [biradsJustSaved, setBiradsJustSaved] = useState(false);

  useEffect(() => {
    const onSaved = () => setBiradsJustSaved(true);
    window.addEventListener("bhi:birads:saved", onSaved);
    return () => window.removeEventListener("bhi:birads:saved", onSaved);
  }, []);

  // ── Derive status from the BHI snapshot ───────────────────────────
  const hasDensity = !!snapshot?.density?.has_data || biradsJustSaved;
  const metabolic = snapshot?.metabolic?.components || {};
  const labFields = ["insulin", "igf1", "inflammation", "estrogen", "adiposity"]
    .filter((k) => metabolic[k]?.score != null);
  const labsStatus = labFields.length >= 4 ? "complete" : labFields.length >= 1 ? "partial" : "missing";

  const benignCount = snapshot?.benign?.findings?.length || 0;
  // iter735 (Feb 2026): previously "profile" pill turned green only if user
  // had benign findings OR family_history flag. That's WRONG — a filled-out
  // demographic / lifestyle profile with no findings should still count as
  // complete. New rule: complete if we have the core demographic + lifestyle
  // signals (age + BMI + at least one lifestyle marker).
  const sigs = snapshot?.signals || {};
  const hasAge = !!sigs.age;
  const hasBMI = !!(sigs.bmi || sigs.height_cm);
  const hasLifestyle = !!(sigs.smoking !== undefined || sigs.alcohol_units_per_week !== undefined || sigs.exercise_minutes_per_week != null);
  const profileComplete = (hasAge && hasBMI && hasLifestyle) || benignCount > 0 || sigs.family_history;
  const profileStatus = profileComplete ? "complete" : "partial";

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  const items = [
    { key: "speclab",   status: "missing",                              data: { label: lang === "bg" ? "DUTCH / ДНК тест" : lang === "es" ? "Test DUTCH / ADN" : "DUTCH / DNA test",
                                                                                desc:  lang === "bg" ? "Качете DUTCH (хормони, методация) или ДНК отчет (MTHFR, COMT, CYP, GST, BRCA) — AI ще извлече структурираните маркери и ще ги визуализира" : lang === "es" ? "Suba DUTCH o informe genómico" : "Upload DUTCH hormone test or genomic report — AI extracts structured markers",
                                                                                cta:   lang === "bg" ? "Качи" : lang === "es" ? "Subir" : "Upload" },
      Icon: Dna, color: "text-purple-500", openUpload: true, hint: "" },
    { key: "mammogram", status: hasDensity ? "complete" : "missing", data: t.mammogram, Icon: ScanLine, color: "text-pink-500" },
    { key: "labs",      status: labsStatus,                           data: t.labs,      Icon: FlaskConical, color: "text-blue-500", openUpload: true, hint: "standard_lab" },
    { key: "profile",   status: profileStatus,                        data: t.profile,   Icon: ClipboardList, color: "text-emerald-500" },
    { key: "epicrisis", status: "missing",                            data: t.epicrisis, Icon: FileText,    color: "text-amber-500", openUpload: true, hint: "epicrisis" },
  ];

  const handleClick = (it) => {
    if (it.openUpload) { setUploadHint(it.hint || ""); setUploadOpen(true); return; }
    const route = it.data.route || "";
    if (route.startsWith("#")) {
      // Scroll to a testid'd target on the SAME page (e.g. manual-birads-card)
      const testid = route.slice(1);
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        try { el.classList.add("ring-2", "ring-pink-400"); setTimeout(() => el.classList.remove("ring-2", "ring-pink-400"), 1800); } catch (_) { /* noop */ }
        return;
      }
    }
    navigate(route);
  };

  const summary = `${items.filter(i => i.status === "complete").length}/${items.length}`;

  return (
    <div className={`${cardCls} p-5 mb-4`} data-testid="my-data-hub">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shrink-0">
          <Database className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-extrabold">{t.title}</h3>
            <span className={`text-xs font-bold ${isDark ? "text-pink-300" : "text-pink-600"}`}>
              {summary}
            </span>
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{t.subtitle}</p>
          <p className="text-[10px] mt-1 font-semibold text-cyan-500">
            Данните са общи за всички модули — качен тук документ се вижда и в бъбречния
            модул, черния дроб и профила. Целият списък е в профила → Моите данни.
          </p>
        </div>
      </div>

      {/* Единна точка за качване — един бутон за всякакви документи */}
      <button
        onClick={() => { setUploadHint(""); setUploadOpen(true); }}
        className="mt-3 w-full px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-95 flex items-center justify-center gap-2 shadow-md"
        data-testid="my-data-upload-any"
      >
        <FileText className="w-4 h-4" />
        {lang === "bg" ? "Качи документ (лаборатория, DUTCH/ДНК, епикриза)"
          : lang === "es" ? "Subir documento (laboratorio, DUTCH/ADN, epicrisis)"
          : "Upload document (labs, DUTCH/DNA, epicrisis)"}
      </button>

      {/* Always-visible quick-status grid */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.map((it) => (
          <button
            key={it.key}
            onClick={() => handleClick(it)}
            className={`p-3 rounded-xl border text-left transition-all hover:scale-[1.02] hover:shadow-md ${
              isDark ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"
            }`}
            data-testid={`my-data-${it.key}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <it.Icon className={`w-4 h-4 ${it.color}`} />
              <StatusPill status={it.status} t={t} isDark={isDark} />
            </div>
            <div className="text-xs font-bold truncate">{it.data.label}</div>
            <div className={`text-[10px] mt-0.5 inline-flex items-center gap-0.5 ${isDark ? "text-pink-300" : "text-pink-600"}`}>
              {it.data.cta} <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        className={`mt-3 text-[11px] font-semibold ${isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-800"}`}
        data-testid="my-data-toggle"
      >
        {open ? `▲ ${t.collapse}` : `▼ ${t.expand}`}
      </button>

      {open && (
        <div className="mt-3 space-y-2" data-testid="my-data-detailed">
          {items.map((it) => (
            <div
              key={it.key}
              className={`p-3 rounded-xl border flex items-start gap-3 ${isDark ? "border-white/10" : "border-gray-200"}`}
            >
              <it.Icon className={`w-5 h-5 ${it.color} shrink-0 mt-0.5`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-bold">{it.data.label}</span>
                  <StatusPill status={it.status} t={t} isDark={isDark} />
                </div>
                <p className={`text-xs leading-snug ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {it.data.desc}
                </p>
              </div>
              <button
                onClick={() => handleClick(it)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:opacity-90 shrink-0 flex items-center gap-1"
              >
                {it.data.cta} <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <UnifiedDocumentUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        lang={lang}
        initialHint={uploadHint}
        onDone={(data) => {
          onSpeclabUploaded?.(data);
          try { window.dispatchEvent(new CustomEvent("bhi:document:uploaded", { detail: data })); } catch (_) { /* noop */ }
        }}
      />
    </div>
  );
}
